import { useState } from "react";
import {
  Phone,
  Mail,
  Instagram,
  MessageCircle,
  MapPin,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

const info = [
  {
    icon: Phone,
    label: "Consultas del Servicio",
    value: "0810-345-1201",
    color: "text-sky-500 bg-sky-50",
  },
  {
    icon: Mail,
    label: "Correo",
    value: "administracion@silicom.com.ar",
    color: "text-pink-500 bg-pink-50",
  },
  {
    icon: Instagram,
    label: "Instagram",
    value: "@silicom.com.ar",
    href: "https://instagram.com/silicom.com.ar",
    color: "text-fuchsia-500 bg-fuchsia-50",
  },
  {
    icon: MessageCircle,
    label: "WhatsApp",
    value: "11-2779-0411",
    color: "text-emerald-500 bg-emerald-50",
  },
  {
    icon: MapPin,
    label: "Oficina Central",
    value: "Leandro N. Alem 734, Merlo, Buenos Aires",
    color: "text-indigo-500 bg-indigo-50",
  },
];

const Contacto = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    plan: "",
    mensaje: "",
  });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value ?? e });

  const submit = (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.telefono || !form.plan) {
      toast({
        title: "Faltan datos",
        description: "Completá todos los campos obligatorios.",
        variant: "destructive",
      });
      return;
    }
    const text = encodeURIComponent(
      `Hola! Soy ${form.nombre}.\nEmail: ${form.email}\nTel: ${form.telefono}\nPlan: ${form.plan}\n\n${form.mensaje || "Me interesa contratar el servicio."}`,
    );
    window.open(`https://wa.me/5491127790411?text=${text}`, "_blank");
    toast({
      title: "¡Enviado!",
      description: "Te redirigimos a WhatsApp para finalizar la solicitud.",
    });
  };

  return (
    <section
      id="contacto"
      className="relative overflow-hidden border-t border-slate-100 bg-gradient-to-b from-white to-pink-50/40 py-14"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/4 top-0 h-96 w-96 rounded-full bg-pink-200/30 blur-3xl" />
        <div className="absolute right-1/4 bottom-0 h-96 w-96 rounded-full bg-sky-200/30 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="mb-12 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-pink-500">
            Contacto
          </p>
          <h2 className="mt-3 text-3xl font-black text-slate-900 md:text-5xl">
            Solicitá tu servicio
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-slate-600">
            Completá el formulario y nos comunicamos con vos en menos de 24 hs.
          </p>
        </div>

        <div className="grid gap-8 lg:grid-cols-5">
          <div className="space-y-3 lg:col-span-2">
            {info.map(({ icon: Icon, label, value, href, color }) => {
              const Wrapper = href ? "a" : "div";
              const wrapperProps = href
                ? { href, target: "_blank", rel: "noreferrer" }
                : {};
              return (
                <Wrapper
                  key={label}
                  {...wrapperProps}
                  className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:border-pink-200 hover:shadow-md"
                >
                  <div
                    className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${color}`}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                      {label}
                    </p>
                    <p className="mt-1 font-semibold text-slate-900">
                      {value}
                    </p>
                  </div>
                </Wrapper>
              );
            })}
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm md:p-8 lg:col-span-3"
          >
            <h3 className="text-xl font-bold text-slate-900">
              Formulario de Solicitud
            </h3>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="text-slate-700">Nombre y Apellido *</Label>
                <Input
                  data-testid="contact-nombre"
                  value={form.nombre}
                  onChange={onChange("nombre")}
                  placeholder="Juan Pérez"
                  className="mt-1 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                />
              </div>
              <div>
                <Label className="text-slate-700">Email *</Label>
                <Input
                  data-testid="contact-email"
                  type="email"
                  value={form.email}
                  onChange={onChange("email")}
                  placeholder="juan@ejemplo.com"
                  className="mt-1 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                />
              </div>
              <div>
                <Label className="text-slate-700">Teléfono *</Label>
                <Input
                  data-testid="contact-telefono"
                  value={form.telefono}
                  onChange={onChange("telefono")}
                  placeholder="11 1234 5678"
                  className="mt-1 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                />
              </div>
              <div>
                <Label className="text-slate-700">Seleccioná un Plan *</Label>
                <Select
                  value={form.plan}
                  onValueChange={(v) => setForm({ ...form, plan: v })}
                >
                  <SelectTrigger
                    data-testid="contact-plan"
                    className="mt-1 border-slate-200 bg-white text-slate-900"
                  >
                    <SelectValue placeholder="Elegí un plan" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-200 bg-white text-slate-900">
                    <SelectItem value="hogar-300">
                      Hogar 300 Mbps · $29.900/mes
                    </SelectItem>
                    <SelectItem value="hogar-500">
                      Hogar Pro 500 Mbps · $39.900/mes
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-slate-700">Mensaje (opcional)</Label>
                <Textarea
                  data-testid="contact-mensaje"
                  value={form.mensaje}
                  onChange={onChange("mensaje")}
                  rows={4}
                  placeholder="Contanos tu consulta o dirección de instalación"
                  className="mt-1 border-slate-200 bg-white text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>

            <Button
              data-testid="contact-submit"
              type="submit"
              className="mt-6 w-full rounded-full bg-gradient-to-r from-pink-500 to-rose-500 py-6 text-base font-bold text-white shadow-lg shadow-pink-200 hover:from-pink-600 hover:to-rose-600"
            >
              <Send className="mr-2 h-4 w-4" /> Enviar Solicitud
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contacto;
